import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react'
import { formatMcap, formatPrice, formatNumber, formatCompact } from '../utils/chartUtils'
import ColorPicker from './ColorPicker'
import useFitToScreen from './hooks/useFitToScreen'
import { openWalletMap, isWalletAddress } from '../utils/walletSources'

// Tape display settings — one global, persisted setting (like log/lin).
// buy/sell null = follow the theme's --up/--down.
const TAPE_SETTINGS_KEY = 'solcheddar:tapeSettings'
const readTapeSettings = () => {
  try { return { flip: false, buy: null, sell: null, ...JSON.parse(localStorage.getItem(TAPE_SETTINGS_KEY) || '{}') } }
  catch { return { flip: false, buy: null, sell: null } }
}

/**
 * TapePanel — the live Trade Tape (recent trades) as a standalone grid panel.
 *
 * Shared state (watched wallets, trade-size filter) is owned by App because
 * the chart also consumes it (wallet highlighting lives in the rows here, but
 * the filter feeds data.setTradeFilterMin and the chart's Wallets tab still
 * reads watchedWallets until it's extracted). TapePanel receives these as
 * props and owns only its own dropdown open/close + input draft state.
 *
 * Props:
 *   data, dataSource
 *   watchedWallets, setWatchedWallets, addWatchedWallet
 *   walletInput, setWalletInput, walletNameInput, setWalletNameInput
 *   tradeFilterMin, setTradeFilterMin
 */
// ─── Lifted rows ─────────────────────────────────────────────────────────
// When one of YOUR trades or a watched wallet's trade is pushed below the
// tape's view, a copy glides up (diagonally, one transform transition) to a
// stack pinned at the top on the OPPOSITE side, holds 30s, then fades.
// Everything moves via transform/opacity only — compositor work, no layout.
const LIFT_HOLD_MS = 30000
const LIFT_FADE_MS = 600
const LIFT_EASE = 'cubic-bezier(0.22, 0.8, 0.2, 1)'

function LiftChip({ L, index, rowH, flip, layerW, fmtAmt, fmtLevel, onGone }) {
  const ref = useRef(null)
  const [phase, setPhase] = useState('start')   // start → in → out
  const [startX, setStartX] = useState(null)    // measured before first paint
  const targetY = index * rowH
  useLayoutEffect(() => {
    // Anchored on the opposite side: right in normal order, left when
    // flipped. Start = where the row left the view: bottom, on the row's own
    // side. Measured here (before paint) so the first frame is already right.
    const w = ref.current?.offsetWidth || 0
    setStartX(flip ? Math.max(0, layerW - w) : -Math.max(0, layerW - w))
    // Two frames after that paint: transition in.
    let r2
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setPhase('in')) })
    const tOut = setTimeout(() => setPhase('out'), LIFT_HOLD_MS)
    const tGone = setTimeout(onGone, LIFT_HOLD_MS + LIFT_FADE_MS + 50)
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); clearTimeout(tOut); clearTimeout(tGone) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const t = L.trade
  const accent = L.kind === 'own' ? '#ffa600' : '#0078ff'
  const transform = phase === 'start'
    ? `translate(${startX ?? 0}px, ${L.fromY}px)`
    : `translate(0px, ${targetY}px)`
  return (
    <div ref={ref} style={{
      position: 'absolute', top: 0, [flip ? 'left' : 'right']: 0,
      display: 'flex', flexDirection: flip ? 'row' : 'row-reverse', alignItems: 'center', gap: 8,
      height: rowH, padding: '0 8px', whiteSpace: 'nowrap', fontSize: 11,
      background: `color-mix(in srgb, ${accent} 14%, #0e0e0e)`,
      [flip ? 'borderLeft' : 'borderRight']: `3px solid ${accent}`,
      borderRadius: 3, boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
      transform,
      opacity: phase === 'out' ? 0 : 1,
      visibility: startX === null ? 'hidden' : 'visible',
      transition: phase === 'start' ? 'none'
        : `transform 700ms ${LIFT_EASE}, opacity ${LIFT_FADE_MS}ms ease`,
      willChange: 'transform, opacity',
    }}>
      <span style={{ color: '#555' }}>{t.time}</span>
      <span style={{ color: t.side === 'buy' ? 'var(--up)' : 'var(--down)', fontWeight: 600 }}>{t.side.toUpperCase()}</span>
      <span style={{ color: '#cacaca' }}>{fmtAmt(t.amount)}</span>
      <span style={{ color: '#888' }}>{fmtLevel(t.mcap)}</span>
      <span style={{ color: accent }}>{L.label}</span>
    </div>
  )
}

export default function TapePanel({
  data, dataSource,
  watchedWallets,                 // read-only here: row highlighting only
  tradeFilterMin, setTradeFilterMin,
}) {
  // Dropdown anchors: null = closed, {x, y} = open at a viewport-clamped
  // position under the icon that was clicked. position:fixed + clamping
  // replaced the old static CSS offsets (right:-180px / -220px), which
  // pushed the menus offscreen once the panel could live in a right-edge
  // workspace cell.
  const [tradeFilterPos, setTradeFilterPos] = useState(null)
  const tradeFilterRef = useRef(null)

  // ── Settings menu (right-click the tape) ──
  const [tapeCfg, setTapeCfg] = useState(readTapeSettings)
  const setCfg = (patch) => setTapeCfg(prev => {
    const next = { ...prev, ...patch }
    try { localStorage.setItem(TAPE_SETTINGS_KEY, JSON.stringify(next)) } catch {}
    return next
  })
  const [tapeMenu, setTapeMenu] = useState(null)   // {x,y} | null
  const tapeMenuRef = useRef(null)
  useFitToScreen(tapeMenuRef, !!tapeMenu, tapeMenu?.x ?? 0, tapeMenu?.y ?? 0)
  useEffect(() => {
    if (!tapeMenu) return
    // Bubble-phase window listeners: the menu and ColorPicker's portal both
    // stopPropagation on mousedown, so clicks inside them don't close it.
    const close = () => setTapeMenu(null)
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('mousedown', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [tapeMenu])

  // Scroll anchoring: the feed is top-anchored (newest row at the top). When a
  // sweep collapses 3+ rows into one, content height shrinks and the browser
  // keeps scrollTop numerically fixed — so the rows under the cursor shift and
  // the whole tape appears to jump. While the user is live-following (at/near
  // the top), re-pin to the top after each render so merges never move the
  // view. If they've scrolled down to read history, leave them alone.
  const scrollRef = useRef(null)
  const followingRef = useRef(true)
  const liftLayerRef = useRef(null)
  const [lifted, setLifted] = useState([])        // [{ id, trade, kind, label, fromY }]
  const liftedIdsRef = useRef(new Set())          // each trade lifts once
  const seenVisibleRef = useRef(new Set())        // was on screen at some point
  const onScroll = () => {
    const el = scrollRef.current
    if (el) followingRef.current = el.scrollTop <= 4
  }

  // Clamp a dropdown of width `w` to the viewport, anchored at the icon's
  // bottom-left. Same clamp pattern as ChartContextMenu / the ws picker.
  const anchorBelow = (el, w, h) => {
    const r = el.getBoundingClientRect()
    return {
      x: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)),
      y: Math.max(8, Math.min(r.bottom + 6, window.innerHeight - h - 8)),
    }
  }

  useEffect(() => {
    const handler = (e) => {
      if (tradeFilterRef.current && !tradeFilterRef.current.contains(e.target)) setTradeFilterPos(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const formatYAxis = (val) => (dataSource === 'hyperliquid' ? formatPrice(val) : formatMcap(val))

  // ── Commit coalescing ──
  // useChartData publishes a FRESH recentTrades array on every WS batch — on
  // a busy futures pair that's a React commit per message, and the tape's
  // ~350-row reconcile (row churn, removeChild, layout, GC pressure) was the
  // largest render cost in an 11s ETH/USDC profile. Nobody reads a tape at
  // message rate: hold the latest array in a ref and flush it into render
  // state at 4Hz. Reference-compare makes idle flushes free (setState with
  // the same array bails), and everything below — max, filter, sweep fold,
  // the rows JSX — derives from the FLUSHED array, so between flushes every
  // memo holds and a parent render reconciles nothing.
  const [tapeTrades, setTapeTrades] = useState(() => data.recentTrades || [])
  const latestTradesRef = useRef(data.recentTrades || [])
  latestTradesRef.current = data.recentTrades || []
  useEffect(() => {
    const id = setInterval(() => {
      setTapeTrades(prev => (prev === latestTradesRef.current ? prev : latestTradesRef.current))
    }, 250)
    return () => clearInterval(id)
  }, [])

  const maxTradeAmount = useMemo(() => {
    let max = 0
    for (const t of tapeTrades) if (t.amount > max) max = t.amount
    return max
  }, [tapeTrades])

  const tradeById = useMemo(() => {
    const m = new Map()
    for (const t of tapeTrades) if (t.isOwn || t.wallet !== 'unknown') m.set(String(t.id), t)
    return m
  }, [tapeTrades])

  const visibleTrades = useMemo(() => (tradeFilterMin > 0
    ? tapeTrades.filter(t => t.amount >= tradeFilterMin)
    : tapeTrades), [tapeTrades, tradeFilterMin])

  // ── Sweep collapsing + dateline grouping ──
  // SWEEPS: 3+ consecutive same-side prints in the same second fold into
  // one row (summed size, ×N count, wallet shown only when it's a single
  // wallet sweeping — the juiciest tell — else "N wallets"). Special rows
  // NEVER fold: own trades, watched wallets, LP/pool events keep their
  // styling and visibility. DATELINES: the timestamp prints once per
  // second-group, newspaper-style; later rows in the group leave the time
  // column empty, and group boundaries get a hairline (CSS .dateline-first).
  const renderRows = useMemo(() => {
    const isSpecial = (t) => {
      if (t.isOwn) return true
      if (t.is_liquidity_event === true || t.trade_type?.startsWith('lp_')) return true
      if (t.trade_type === 'pool_creation') return true
      if (t.wallet !== 'unknown' && watchedWallets.some(w => t.wallet.startsWith(w.address.slice(0, 8)))) return true
      return false
    }
    const rows = []
    let i = 0
    while (i < visibleTrades.length) {
      const t = visibleTrades[i]
      if (!isSpecial(t)) {
        let j = i + 1
        while (
          j < visibleTrades.length &&
          visibleTrades[j].side === t.side &&
          visibleTrades[j].time === t.time &&
          !isSpecial(visibleTrades[j])
        ) j++
        const n = j - i
        // Build-forward fold: the NEWEST group (the run that starts at index 0,
        // i.e. the in-progress second at the top) always folds into ONE row,
        // even at count 1, so it never renders as individual rows that later
        // collapse — that collapse was the "jump up" when watching live at the
        // top. Older, settled groups keep the >=3 threshold (they don't change,
        // so no jump risk). The ×N badge still only appears at >=3, so a single
        // newest trade looks like a normal row but is structurally the same row
        // that grows as more prints land in its second.
        const isNewestGroup = i === 0
        if (n >= 3 || isNewestGroup) {
          const slice = visibleTrades.slice(i, j)
          const uniq = new Set(slice.map(x => x.wallet)).size
          rows.push({
            kind: 'sweep',
            id: `sw-${t.id}`,
            side: t.side,
            time: t.time,
            amount: slice.reduce((s, x) => s + x.amount, 0),
            mcap: t.mcap,                       // newest print's level
            count: n,
            singleWallet: uniq === 1 ? t.wallet : null,
            walletCount: uniq,
          })
          i = j
          continue
        }
      }
      rows.push({ kind: 'trade', trade: t, id: t.id })
      i++
    }
    let lastTime = null
    for (const r of rows) {
      const tm = r.kind === 'sweep' ? r.time : r.trade.time
      r.showTime = tm !== lastTime
      lastTime = tm
    }
    return rows
  }, [visibleTrades, watchedWallets])

  // After the rows recompute (including sweep merges that shrink height),
  // re-pin to the top if the user was live-following. useLayoutEffect runs
  // before paint, so there's no visible jump.
  useLayoutEffect(() => {
    if (followingRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
    // Lift detection: a special row (own / watched) that WAS on screen and has
    // now been pushed below the view. Live-following only — scrolling through
    // history isn't "dropping out", and neither is initial load (never seen).
    const el = scrollRef.current
    if (!el || !followingRef.current) return
    const c = el.getBoundingClientRect()
    const nodes = el.querySelectorAll('.trade-row.own-trade, .trade-row.watched-trade')
    const add = []
    nodes.forEach(n => {
      const id = n.dataset.tid
      if (!id) return
      const r = n.getBoundingClientRect()
      if (r.bottom > c.top && r.top < c.bottom) { seenVisibleRef.current.add(id); return }
      if (r.top >= c.bottom && seenVisibleRef.current.has(id) && !liftedIdsRef.current.has(id)) {
        const trade = tradeById.get(id)
        if (!trade) return
        liftedIdsRef.current.add(id)
        const own = !!trade.isOwn
        const mw = own ? null : watchedWallets.find(w => trade.wallet.startsWith(w.address.slice(0, 8)))
        add.push({
          id, trade, kind: own ? 'own' : 'watched',
          label: own ? 'YOU' : (mw?.label || `${trade.wallet.slice(0, 4)}...${trade.wallet.slice(-4)}`),
          fromY: Math.max(0, c.height - r.height),
        })
      }
    })
    if (add.length) setLifted(prev => [...prev, ...add])
    // Keep the bookkeeping bounded on long sessions.
    if (seenVisibleRef.current.size > 2000) seenVisibleRef.current = new Set([...seenVisibleRef.current].slice(-500))
    if (liftedIdsRef.current.size > 2000) liftedIdsRef.current = new Set([...liftedIdsRef.current].slice(-500))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderRows])

  const flip = !!tapeCfg.flip
  const rowDir = flip ? 'row-reverse' : undefined
  const barSide = flip ? { right: 0 } : { left: 0 }

  return (
    <div
      className={`tape-panel${flip ? ' tape-flip' : ''}`}
      // Custom colours override the theme vars for this panel only — the
      // side labels and size bars both read --up/--down.
      style={{
        ...(tapeCfg.buy ? { '--up': tapeCfg.buy } : null),
        ...(tapeCfg.sell ? { '--down': tapeCfg.sell } : null),
      }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setTapeMenu({ x: e.clientX + 4, y: e.clientY }) }}
    >
      {/* Header: title + filter + wallet-watchlist controls */}
      <div className="tape-panel-header">
        <span className="tape-panel-title">Tape</span>
        <span className="tape-filter-wrapper" ref={tradeFilterRef}>
          <img className="tape-filter" src="/filter.png" alt="filter" width="16" height="16"
            onClick={(e) => {
              e.stopPropagation()
              setTradeFilterPos(prev => prev ? null : anchorBelow(e.currentTarget, 224, 110))
            }} />
          {tradeFilterPos && (
            <div className="trade-filter-dropdown"
              style={{ position: 'fixed', left: tradeFilterPos.x, top: tradeFilterPos.y, right: 'auto', margin: 0 }}>
              <div className="trade-filter-label">Min Size: <span>{tradeFilterMin > 0 ? formatNumber(tradeFilterMin) : 'Off'}</span></div>
              <input type="range" min="0" max="10000000" step="1000" value={tradeFilterMin}
                onChange={(e) => setTradeFilterMin(Number(e.target.value))} className="trade-filter-slider" />
              <div className="trade-filter-range"><span>0</span><span>10M</span></div>
            </div>
          )}
        </span>
      </div>

      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="trades-container" ref={scrollRef} onScroll={onScroll}>
        {useMemo(() => renderRows.map((row) => {
          if (row.kind === 'sweep') {
            // A newest-group fold can have count 1-2 (it folds early to avoid
            // the collapse jump). Only dress it as a SWEEP — special styling +
            // ×N badge — once it's a real sweep (>=3); below that it renders
            // like a normal trade row.
            const isRealSweep = row.count >= 3
            return (
              <div
                key={row.id}
                className={`trade-row ${isRealSweep ? 'sweep-row ' : ''}${row.side}${row.showTime ? ' dateline-first' : ''}`}
                style={{ position: 'relative', isolation: 'isolate', flexDirection: rowDir }}
                title={isRealSweep ? `${row.count} ${row.side}s in one second${row.singleWallet ? ' — single wallet' : ` — ${row.walletCount} wallets`}` : undefined}
              >
                {maxTradeAmount > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    ...barSide,
                    bottom: 0,
                    width: `${Math.min(100, (row.amount / maxTradeAmount) * 100)}%`,
                    background: row.side === 'buy' ? 'color-mix(in srgb, var(--up) 12%, transparent)' : 'color-mix(in srgb, var(--down) 12%, transparent)',
                    pointerEvents: 'none',
                    zIndex: -1,
                  }} />
                )}
                <span className="time">{row.showTime ? row.time : ''}</span>
                <span className="side">{row.side.toUpperCase()}</span>
                <span className="amount">
                  {dataSource === 'hyperliquid' ? formatCompact(row.amount) : formatNumber(row.amount)}
                  {isRealSweep && <span className="sweep-count">×{row.count}</span>}
                </span>
                <span className="mcap">{formatYAxis(row.mcap)}</span>
                <span className="wallet">
                  {row.singleWallet
                    ? (row.singleWallet === 'unknown'
                        ? (dataSource === 'hyperliquid' ? 'Hyperliquid' : 'unknown')
                        : `${row.singleWallet.slice(0, 4)}...${row.singleWallet.slice(-4)}`)
                    : `${row.walletCount} wallets`}
                </span>
              </div>
            )
          }
          const trade = row.trade
          const matchedWallet = trade.wallet !== 'unknown'
            ? watchedWallets.find(w => trade.wallet.startsWith(w.address.slice(0, 8)))
            : null
          const isWatched = !!matchedWallet
          const isLP = trade.is_liquidity_event === true || trade.trade_type?.startsWith('lp_')
          const isPool = trade.trade_type === 'pool_creation'

          return (
            <div
              key={trade.id}
              data-tid={(trade.isOwn || isWatched) ? String(trade.id) : undefined}
              className={`trade-row ${trade.side}${trade.isOwn ? ' own-trade' : ''}${isWatched ? ' watched-trade' : ''}${isLP || isPool ? ' lp-trade' : ''}${row.showTime ? ' dateline-first' : ''}`}
              style={{ position: 'relative', isolation: 'isolate', flexDirection: rowDir }}
            >
              {maxTradeAmount > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  ...barSide,
                  bottom: 0,
                  width: `${(trade.amount / maxTradeAmount) * 100}%`,
                  background: trade.side === 'buy' ? 'color-mix(in srgb, var(--up) 12%, transparent)' : 'color-mix(in srgb, var(--down) 12%, transparent)',
                  pointerEvents: 'none',
                  zIndex: -1,
                }} />
              )}
              <span className="time">{row.showTime ? trade.time : ''}</span>
              <span className="side">{trade.side.toUpperCase()}</span>
              <span className="amount">{dataSource === 'hyperliquid' ? formatCompact(trade.amount) : formatNumber(trade.amount)}</span>
              <span className="mcap">{formatYAxis(trade.mcap)}</span>
              <span className="wallet"
                title={isWalletAddress(trade.wallet) ? `${trade.wallet}\nclick for holdings + interaction map` : trade.wallet}
                style={isWalletAddress(trade.wallet) ? { cursor: 'pointer' } : undefined}
                onClick={isWalletAddress(trade.wallet) ? (e) => { e.stopPropagation(); openWalletMap(trade.wallet) } : undefined}>
                {trade.wallet === 'Hyperliquid' || trade.wallet === 'Tick-Stream' || trade.wallet === 'Bitunix' || trade.wallet === 'Coinbase'
                  ? trade.wallet
                  : trade.wallet === 'unknown'
                    ? (dataSource === 'hyperliquid' ? 'Hyperliquid' : 'unknown')
                    : (matchedWallet && matchedWallet.label)
                      ? matchedWallet.label
                      : `${trade.wallet.slice(0, 4)}...${trade.wallet.slice(-4)}`}
                {isLP && ' [LP]'}
                {isPool && ' [POOL CREATED]'}
              </span>
            </div>
          )
        // Rows re-materialize only when a flush (or filter/source/watch
        // change) actually moves them; other parent renders hand React the
        // SAME element array and reconciliation is a no-op.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }), [renderRows, maxTradeAmount, dataSource, watchedWallets, flip])}
      </div>
      {/* Lift layer: pinned over the tape, never takes input. */}
      <div ref={liftLayerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {lifted.map((L, i) => (
          <LiftChip key={L.id} L={L} index={i} rowH={20} flip={flip}
            layerW={liftLayerRef.current?.clientWidth || 0}
            fmtAmt={(v) => (dataSource === 'hyperliquid' ? formatCompact(v) : formatNumber(v))}
            fmtLevel={formatYAxis}
            onGone={() => setLifted(prev => prev.filter(x => x.id !== L.id))} />
        ))}
      </div>
      </div>

      {tapeMenu && (
        <div
          ref={tapeMenuRef}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation() }}
          style={{
            position: 'fixed', left: tapeMenu.x, top: tapeMenu.y, zIndex: 1000,
            background: '#16181c', border: '1px solid #2a2d33', borderRadius: 6,
            padding: 4, minWidth: 160, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            fontSize: 12, color: '#ddd', userSelect: 'none',
          }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 8px 2px' }}>
            Columns
          </div>
          {[[false, 'Time → Wallet'], [true, 'Wallet → Time']].map(([v, label]) => (
            <div key={label}
              onClick={() => { setCfg({ flip: v }); setTapeMenu(null) }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '5px 8px', borderRadius: 4, cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#22252b' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}>
              <span>{label}</span>
              {flip === v && <span style={{ color: '#44cc77', fontSize: 12 }}>✓</span>}
            </div>
          ))}
          <div style={{ fontSize: 8, fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 8px 2px', borderTop: '1px solid #2a2d33', marginTop: 4 }}>
            Colors
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 6px' }}>
            <ColorPicker
              value={tapeCfg.buy || getComputedStyle(document.documentElement).getPropertyValue('--up').trim() || '#00cc6a'}
              onChange={(c) => setCfg({ buy: c })}
              title="Buy" />
            <ColorPicker
              value={tapeCfg.sell || getComputedStyle(document.documentElement).getPropertyValue('--down').trim() || '#d9505b'}
              onChange={(c) => setCfg({ sell: c })}
              title="Sell" />
            <button
              onClick={() => setCfg({ buy: null, sell: null })}
              title="Follow the theme's buy/sell colours"
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#666', fontSize: 10, fontWeight: 600, cursor: 'pointer', padding: '2px 4px' }}>
              THEME
            </button>
          </div>
        </div>
      )}
    </div>
  )
}